import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Wifi, WifiOff } from 'lucide-react';

export default function OfflineStatusNotifier() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showStatus, setShowStatus] = useState(false);
  const [statusType, setStatusType] = useState<'online' | 'offline'>('online');

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setStatusType('online');
      setShowStatus(true);
      
      // Auto-hide the "Back Online" message after 3.5 seconds
      const timer = setTimeout(() => {
        setShowStatus(false);
      }, 3500);
      return () => clearTimeout(timer);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setStatusType('offline');
      setShowStatus(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check (if user starts offline, show status immediately)
    if (!navigator.onLine) {
      setStatusType('offline');
      setShowStatus(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <AnimatePresence>
      {showStatus && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-sm pointer-events-none">
          {statusType === 'offline' ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-amber-600 text-white px-4 py-3 rounded-xl shadow-xl flex items-center gap-3 border border-amber-500/30 pointer-events-auto"
            >
              <div className="p-1.5 bg-white/20 rounded-lg shrink-0">
                <WifiOff className="w-4 h-4 text-white animate-bounce" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-xs">Offline Mode / आप ऑफलाइन हैं</p>
                <p className="text-[10px] text-amber-100 mt-0.5 font-medium leading-tight">
                  Showing cached product prices. ऑर्डर बुकिंग चालू रहेगी।
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-emerald-600 text-white px-4 py-3 rounded-xl shadow-xl flex items-center gap-3 border border-emerald-500/30 pointer-events-auto"
            >
              <div className="p-1.5 bg-white/20 rounded-lg shrink-0">
                <Wifi className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-xs">Back Online / इंटरनेट चालू है</p>
                <p className="text-[10px] text-emerald-100 mt-0.5 font-medium leading-tight">
                  Priselist updated with live store prices.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </AnimatePresence>
  );
}
